import type { ReactNode } from "react";
import { Icon } from "../icons/Icon";
import { Logo } from "./AppShell";

/** The device notifications round (2026-11-01; brief docs/briefs/device-notifications-round.md rule 2, the red team's findings 8 and 13): a
 *  small drawing of a notification as a device shows it, so the person sees what "Turn on" turns on. Six looks: a banner at the top right of a
 *  Mac, a toast at the bottom right of Windows, a card on an iPhone's lock screen, a row in an Android phone's notification shade, the iPhone's
 *  Share sheet with "Add to Home Screen" (`homescreen`, for an iPhone or iPad in a browser tab) and a plain card (`other`). CSS only: no picture
 *  file, no motion, semantic tokens (the phone screens follow the inverse colours, light at night like the toasts). Decorative: the panel's
 *  words say the same, so the drawing is hidden from screen readers. Props in: the sample's words and the icon (the Omna mark by default). */
export type DeviceStageKind = "mac" | "windows" | "iphone" | "android" | "homescreen" | "other";
export interface DeviceStageProps {
  kind: DeviceStageKind;
  /** the sample's title, sender line and body ("Assigned to you by Mia", "OmnaBase", "Login fails after the update") */
  title: string; sender: string; body: string;
  /** the notification's icon: the Omna mark unless given (a workspace's logo on an iPhone's Home Screen app) */
  icon?: ReactNode;
}

export function DeviceStage({ kind, title, sender, body, icon }: DeviceStageProps) {
  return (
    <div className={`ob-stage ob-stage--${kind}`} aria-hidden="true">
      <div className="ob-stage__screen">
        {kind === "homescreen" ? <ShareSheet /> : (
          <>
            {kind === "iphone" && <span className="ob-stage__clock">9:41</span>}
            <div className="ob-stage__note">
              <span className="ob-stage__icon">{icon ?? <Logo />}</span>
              <span className="ob-stage__text">
                <span className="ob-stage__sender">{sender}</span>
                <span className="ob-stage__title">{title}</span>
                <span className="ob-stage__body">{body}</span>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** the iPhone's Share sheet: Share, then "Add to Home Screen" (the second row marked as the one to tap) */
function ShareSheet() {
  return (
    <div className="ob-stage__sheet">
      <span className="ob-stage__step"><Icon name="share" size="sm" />Share</span>
      <span className="ob-stage__step ob-stage__step--next"><Icon name="addToHome" size="sm" />Add to Home Screen</span>
    </div>
  );
}
